import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { EventService } from '../core/services/event.service';
import { calcStorageEstimate, SHOW_STORAGE_BANNER_FOR_ALL } from './storage-upgrade-banner.utils';

const GB_IN_BYTES = 1024 * 1024 * 1024;

/** Plan names (lowercased) that are considered free/basic tier. */
const FREE_PLAN_NAMES = new Set(['free', 'free account', 'basic', 'personal-basic', 'personal basic']);

@Component({
  selector: 'app-storage-upgrade-banner',
  templateUrl: './storage-upgrade-banner.component.html',
  styleUrls: ['./storage-upgrade-banner.component.css']
})
export class StorageUpgradeBannerComponent implements OnInit, OnChanges {
  /**
   * Optional: pass pre-fetched values (in GB, matching plan.total_storage_gb /
   * plan.available_storage_gb from the account details API) to skip the fetch.
   */
  @Input() usedStorageGB?: number;
  @Input() totalStorageGB?: number;

  isLoading = true;
  isFreePlan = false;

  _usedGB = 0;
  _totalGB = 0;
  photosLeft = 0;
  videoMinsLeft = 0;

  constructor(private eventService: EventService, private router: Router) {}

  ngOnInit(): void {
    if (this.usedStorageGB !== undefined && this.totalStorageGB !== undefined) {
      // When storage is passed in, plan name is unknown — rely on SHOW_STORAGE_BANNER_FOR_ALL or pass planName input
      this.applyValues(this.usedStorageGB, this.totalStorageGB, '');
    } else {
      this.fetchStorageInfo();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['usedStorageGB'] || changes['totalStorageGB']) &&
      this.usedStorageGB !== undefined &&
      this.totalStorageGB !== undefined
    ) {
      this.applyValues(this.usedStorageGB, this.totalStorageGB, '');
    }
  }

  private applyValues(usedGB: number, totalGB: number, planName: string): void {
    const nameMatch = FREE_PLAN_NAMES.has(planName.trim().toLowerCase());
    this.isFreePlan = SHOW_STORAGE_BANNER_FOR_ALL || nameMatch;
    this._usedGB = usedGB;
    this._totalGB = totalGB;
    if (this.isFreePlan) {
      const availableBytes = Math.max(0, totalGB - usedGB) * GB_IN_BYTES;
      const est = calcStorageEstimate(availableBytes);
      this.photosLeft = est.photos;
      this.videoMinsLeft = est.videoMins;
    }
    this.isLoading = false;
  }

  private async fetchStorageInfo(): Promise<void> {
    try {
      (await this.eventService.getMyAccountDetails()).subscribe({
        next: (res: any) => {
          const planName: string = res?.plan?.plan_name ?? '';
          const totalGB: number = res?.plan?.total_storage_gb ?? 2;
          const availableGB: number = res?.plan?.available_storage_gb ?? 0;
          const usedGB = Math.max(0, totalGB - availableGB);
          this.applyValues(usedGB, totalGB, planName);
        },
        error: () => {
          this.isLoading = false;
        }
      });
    } catch {
      this.isLoading = false;
    }
  }

  get usedGB(): string {
    return this._usedGB.toFixed(2);
  }

  get totalGB(): string {
    return this._totalGB.toFixed(2);
  }

  /** Percentage of storage used, clamped 0–100. */
  get usagePercent(): number {
    if (!this._totalGB) return 0;
    return Math.min(100, Math.round((this._usedGB / this._totalGB) * 100));
  }

  navigateToUpgrade(): void {
    this.router.navigate(['/subscription-action'], { queryParams: { action: 'upgrade' } });
  }
}
